import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface OpsGenieFormProps {
  serverErrors?: ServerErrors;
}

export const OpsGenieForm = ({serverErrors}: OpsGenieFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.api_endpoint"
            label="API Endpoint"
            required={true}
            tooltip="Listed on the Integrations / Configured Integrations page in OpsGenie."
            maxLength={255}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.api_key"
            label="API Key"
            required={true}
            tooltip="Listed on the Integrations / Configured Integrations page in OpsGenie."
            maxLength={36}
            autoFocus={true}
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.teams"
            label="Teams"
            required={false}
            tooltip="A comma separated list of team names which will be responsible for the alert."
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.tags"
            label="Tags"
            required={false}
            tooltip="A comma separated list of labels attached to the alert. You may overwrite the quiet hours setting for urgent alerts by adding the OverwriteQuietHours tag. Leave blank to automatically pull the tags from the check instead."
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <SwitchField
            name="settings.autoresolve"
            label="Auto-resolve Incident"
            tooltip="Automatically resolve incident once the check is back up."
          />
        </div>
      </div>
      <SetupInfo>
        <div className="p-2">
          Go to{" "}
          <a href="https://app.opsgenie.com/integration#/" target="_blank">
            {" "}
            Integrations
          </a>
          , and create a new "Uptime" integration. Then copy the "API Endpoint" and "API Key" into the corresponding
          fields above.
        </div>
        <div className="p-2">
          <a target="_blank" href="https://support.uptime.com/hc/en-us/articles/360002058819-Configuring-Opsgenie">
            View {IntegrationModuleInfo[IntegrationModules.OPSGENIE].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
